import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import { Calendar, Tag as TagIcon, Loader2, AlertCircle, ArrowLeft } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import Header from '../components/Header';
import Footer from '../components/Footer';

const TagPage = () => {
  const { tag } = useParams();
  const [noticias, setNoticias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchNoticiasPorTag = async () => {
      try {
        setLoading(true);
        setError(null);

        // Tentar buscar por tags (array-contains) + ordenação decrescente
        // Se o Firebase solicitar Índice Composto (Composite Index), crie-o no console.
        let q = query(
          collection(db, 'noticias'),
          where('tags', 'array-contains', tag),
          orderBy('data', 'desc')
        );

        let snapshot;
        try {
          snapshot = await getDocs(q);
        } catch (idxErr) {
          if (idxErr.code === 'failed-precondition') {
            console.warn('Crie o índice composto no Firebase Console. Link no erro:', idxErr.message);
          }
          q = query(collection(db, 'noticias'), where('tags', 'array-contains', tag));
          snapshot = await getDocs(q);
        }

        if (snapshot.empty) {
          q = query(
            collection(db, 'noticias'),
            where('categoria', '==', tag),
            orderBy('data', 'desc')
          );
          try {
            snapshot = await getDocs(q);
          } catch (idxErr2) {
            q = query(collection(db, 'noticias'), where('categoria', '==', tag));
            snapshot = await getDocs(q);
          }
        }

        const lista = snapshot.docs.map((doc) => {
          const d = doc.data();
          const dataVal = d.data;
          const dataStr =
            dataVal && typeof dataVal.toDate === 'function'
              ? dataVal.toDate().toISOString()
              : dataVal
                ? (typeof dataVal === 'string' ? dataVal : null)
                : null;
          return {
            id: doc.id,
            _id: doc.id,
            titulo: d.titulo || '',
            resumo: d.resumo || '',
            link: d.link || '',
            categoria: d.categoria || 'Direito',
            fonte: d.fonte || 'Conjur',
            data: dataStr || new Date().toISOString(),
            tags: d.tags || []
          };
        });

        setNoticias(lista);
      } catch (err) {
        console.error('Erro ao carregar notícias por tag:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchNoticiasPorTag();
  }, [tag]);

  return (
    <>
      <Header />
      <main className="min-h-screen bg-[#F8FAFC]">
        {/* Hero Section */}
        <section className="bg-gradient-to-b from-[#0F172A] to-[#1e293b] text-white py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-6">
            <Link
              to="/noticias"
              className="inline-flex items-center gap-2 text-white hover:text-[#B49163] transition-colors mb-6"
            >
              <ArrowLeft size={20} />
              Voltar para todas as notícias
            </Link>
            <h1 className="text-4xl md:text-5xl font-serif font-bold mb-4">
              Notícias sobre: {tag}
            </h1>
            <p className="text-lg text-gray-300">
              {loading ? 'Carregando...' : `${noticias.length} ${noticias.length === 1 ? 'notícia encontrada' : 'notícias encontradas'}`}
            </p>
          </div>
        </section>

        {/* Conteúdo */}
        <section className="py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-6">
            {loading && (
              <div className="flex flex-col items-center justify-center py-16">
                <Loader2 className="w-12 h-12 text-[#B49163] animate-spin mb-4" />
                <p className="text-gray-600 text-lg">Carregando notícias...</p>
              </div>
            )}

            {error && (
              <Alert variant="destructive" className="bg-red-50 border-red-200 mb-6">
                <AlertCircle className="h-5 w-5" />
                <AlertDescription className="ml-2">
                  <strong>Erro:</strong> {error}
                </AlertDescription>
              </Alert>
            )}

            {!loading && !error && noticias.length === 0 && (
              <div className="text-center py-16">
                <p className="text-gray-600 text-lg mb-4">
                  Nenhuma notícia encontrada com a tag "{tag}"
                </p>
                <Link
                  to="/noticias"
                  className="inline-flex items-center gap-2 text-[#B49163] hover:text-[#0F172A] font-semibold"
                >
                  Ver todas as notícias
                </Link>
              </div>
            )}

            {!loading && !error && noticias.length > 0 && (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {noticias.map((noticia) => (
                  <Link
                    key={noticia.id}
                    to={`/noticias/${noticia.id}`}
                    className="block"
                  >
                    <Card className="overflow-hidden hover:shadow-xl transition-shadow duration-300 border-0 group h-full cursor-pointer">
                      <CardContent className="p-6 bg-white h-full flex flex-col">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <TagIcon size={16} className="text-[#B49163]" />
                            <span className="text-sm text-[#B49163] font-semibold">
                              {noticia.categoria}
                            </span>
                          </div>
                          <span className="text-xs text-gray-500 font-medium">
                            {noticia.fonte}
                          </span>
                        </div>

                        <h3 className="text-xl font-serif font-semibold text-[#0F172A] mb-3 line-clamp-2 group-hover:text-[#B49163] transition-colors">
                          {noticia.titulo}
                        </h3>

                        <p className="text-gray-600 leading-relaxed mb-4 line-clamp-3 flex-grow">
                          {noticia.resumo}
                        </p>

                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 text-sm text-gray-500">
                            <Calendar size={16} />
                            <span>
                              {noticia.data
                                ? `${new Date(noticia.data).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })} às ${new Date(noticia.data).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`
                                : '—'
                              }
                            </span>
                          </div>

                          <span className="text-sm text-[#B49163] font-medium group-hover:text-[#0F172A] transition-colors">
                            Ler mais →
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default TagPage;
